import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Form,
  Input,
  Upload,
  Button,
  Typography,
  Alert,
  Space,
  App as AntApp,
  Divider,
} from 'antd';
import { InboxOutlined, DownloadOutlined, KeyOutlined, SyncOutlined } from '@ant-design/icons';
import { useSession } from '@/hooks/useSession';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function postBinary(endpoint: string, fd: FormData): Promise<Response> {
  return fetch(`/api/v1${endpoint}`, {
    method: 'POST',
    body: fd,
    credentials: 'include',
  });
}

export default function VendorAccountPage() {
  const { t } = useTranslation();
  const { user } = useSession();
  const { message } = AntApp.useApp();

  const [pwFile, setPwFile] = useState<File | null>(null);
  const [pwLoading, setPwLoading] = useState(false);
  const [renewFile, setRenewFile] = useState<File | null>(null);
  const [renewLoading, setRenewLoading] = useState(false);

  const [pwForm] = Form.useForm();
  const [renewForm] = Form.useForm();

  const handlePasswordChange = async (values: any) => {
    if (!pwFile) {
      message.warning('Upload current P12 first');
      return;
    }
    setPwLoading(true);
    const fd = new FormData();
    fd.append('certificate', pwFile);
    fd.append('oldPassword', values.oldPassword);
    fd.append('newPassword', values.newPassword);
    const resp = await postBinary('/auth/password/change', fd);
    if (!resp.ok) {
      try {
        const err = await resp.json();
        message.error(err.message || err.Result || `HTTP ${resp.status}`);
      } catch {
        message.error(`HTTP ${resp.status}`);
      }
      setPwLoading(false);
      return;
    }
    const blob = await resp.blob();
    const serial = resp.headers.get('X-Cert-Serial') ?? 'new';
    downloadBlob(blob, `${user?.commonName ?? 'cert'}-${serial.slice(0, 8)}.p12`);
    message.success('Password changed. New P12 downloaded. Please log in again.');
    pwForm.resetFields();
    setPwFile(null);
    setTimeout(() => (window.location.href = '/login'), 2000);
    setPwLoading(false);
  };

  const handleRenew = async (values: any) => {
    if (!renewFile) {
      message.warning('Upload current P12 first');
      return;
    }
    setRenewLoading(true);
    const fd = new FormData();
    fd.append('certificate', renewFile);
    fd.append('password', values.password);
    if (values.newPassword) fd.append('newPassword', values.newPassword);
    const resp = await postBinary('/auth/renew-p12', fd);
    if (!resp.ok) {
      try {
        const err = await resp.json();
        message.error(err.message || err.Result || `HTTP ${resp.status}`);
      } catch {
        message.error(`HTTP ${resp.status}`);
      }
      setRenewLoading(false);
      return;
    }
    const blob = await resp.blob();
    const serial = resp.headers.get('X-Cert-Serial') ?? 'renewed';
    const graceUntil = resp.headers.get('X-Old-Cert-RevokedAt');
    downloadBlob(blob, `${user?.commonName ?? 'cert'}-renewed-${serial.slice(0, 8)}.p12`);
    message.success(
      graceUntil
        ? `Renewed. Old cert valid until ${new Date(graceUntil).toLocaleString()}`
        : 'Renewed',
    );
    renewForm.resetFields();
    setRenewFile(null);
    setRenewLoading(false);
  };

  return (
    <>
      <Title level={3} style={{ marginTop: 0 }}>
        {t('vendor.accountTitle')}
      </Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={`Signed in as ${user?.displayName || user?.commonName} (${user?.role})`}
      />

      <Card title={<Space><KeyOutlined /> {t('vendor.changePassword')}</Space>} style={{ marginBottom: 16 }}>
        <Paragraph type="secondary">
          Uploads current P12, issues a new cert packaged with the new password, and revokes the old serial immediately.
          You'll be logged out after success — log in again with the new P12.
        </Paragraph>
        <Dragger
          multiple={false}
          maxCount={1}
          beforeUpload={(f) => {
            setPwFile(f);
            return false;
          }}
          onRemove={() => setPwFile(null)}
          accept=".p12,.pfx"
        >
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">Drop current P12 here</p>
        </Dragger>
        <Divider />
        <Form form={pwForm} layout="vertical" onFinish={handlePasswordChange}>
          <Form.Item
            label="Current password"
            name="oldPassword"
            rules={[{ required: true }]}
          >
            <Input.Password autoComplete="off" />
          </Form.Item>
          <Form.Item
            label="New password"
            name="newPassword"
            rules={[{ required: true, min: 12, message: 'At least 12 characters' }]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={pwLoading} icon={<DownloadOutlined />}>
            {t('vendor.changeDownload')}
          </Button>
        </Form>
      </Card>

      <Card title={<Space><SyncOutlined /> {t('vendor.renewP12')}</Space>}>
        <Paragraph type="secondary">
          Issues a new cert with up to 1-year validity. Old P12 remains valid for 7 days as grace.
          New password is optional — if blank, the new P12 uses the same password as the current one.
        </Paragraph>
        <Dragger
          multiple={false}
          maxCount={1}
          beforeUpload={(f) => {
            setRenewFile(f);
            return false;
          }}
          onRemove={() => setRenewFile(null)}
          accept=".p12,.pfx"
        >
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">Drop current P12 here</p>
        </Dragger>
        <Divider />
        <Form form={renewForm} layout="vertical" onFinish={handleRenew}>
          <Form.Item label="Current password" name="password" rules={[{ required: true }]}>
            <Input.Password autoComplete="off" />
          </Form.Item>
          <Form.Item
            label="New password (optional)"
            name="newPassword"
            rules={[
              {
                validator: (_, v) => {
                  if (!v) return Promise.resolve();
                  return v.length >= 12
                    ? Promise.resolve()
                    : Promise.reject(new Error('At least 12 characters'));
                },
              },
            ]}
          >
            <Input.Password autoComplete="new-password" placeholder="Leave blank to reuse" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={renewLoading} icon={<DownloadOutlined />}>
            {t('vendor.renewDownload')}
          </Button>
        </Form>
      </Card>
    </>
  );
}
